import { useState } from "react";
import { AlertTriangle, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SAMPLE = [
  {
    finding: "Business hours don't match across listings",
    why: "When your Google Business Profile, website footer, and social bios show different hours, customers lose trust and search engines lose confidence in your information.",
    priority: "High",
    action: "Pick one source of truth for hours and align it across your Google profile, website, and social bios this week.",
    limitation: "This is an illustrative pattern — we have not viewed your actual listings. Confirming it requires your Google Business Profile link or website.",
  },
  {
    finding: "Mobile pages may be loading slowly",
    why: "Most local customers browse on phones. Slow mobile pages lose visitors before they ever see your offer or your phone number.",
    priority: "High",
    action: "Run your homepage through a free page-speed tool and fix the largest images and scripts first.",
    limitation: "Illustrative only — confirming load times requires a live scan of your website.",
  },
  {
    finding: "No single, consistent call to action",
    why: "Visitors decide quickly. Without one clear next step — call, book, or buy — interested people leave without acting.",
    priority: "Medium",
    action: "Choose one primary action for your business and repeat it on every page and profile.",
    limitation: "Needs a review of your actual pages to confirm. Send your website URL with your intake.",
  },
  {
    finding: "Customer reviews going unanswered",
    why: "Responding to reviews — good and bad — signals an active, attentive business to both customers and search engines.",
    priority: "Medium",
    action: "Reply to your most recent reviews today, then set a weekly review-response habit.",
    limitation: "Volume and recency of your reviews can only be confirmed with access to your review profiles.",
  },
];

const PRIORITY_STYLES = {
  High: "text-red-300 border-red-400/40 bg-red-400/10",
  Medium: "text-gold border-gold/40 bg-gold/10",
};

export const SamplePreviewModal = ({ open, onOpenChange, testid = "sample-preview" }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={`${testid}-modal`}
      className="bg-obsidian-800 border-gold/25 text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl"
    >
      <DialogHeader className="space-y-2">
        <DialogTitle className="font-display text-xl text-white">What an assessment looks like</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">
          A real assessment reviews your actual digital presence and returns findings like these,
          with priorities and next actions — delivered to your portal.
        </DialogDescription>
      </DialogHeader>

      <div
        className="flex gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3"
        data-testid={`${testid}-banner`}
      >
        <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
        <p className="text-xs text-slate-200 leading-relaxed">
          <span className="text-gold font-semibold">Illustrative sample — not an assessment of your business.</span>{" "}
          These are example findings showing the format. No customer results or testimonials are shown.
        </p>
      </div>

      <div className="space-y-4">
        {SAMPLE.map((s, i) => (
          <div key={i} data-testid={`${testid}-finding-${i + 1}`} className="rounded-2xl border border-slate-700/60 bg-obsidian-900/60 p-5">
            <div className="flex items-start justify-between gap-3">
              <h4 className="font-display font-bold text-white text-sm">{s.finding}</h4>
              <span className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-widest ${PRIORITY_STYLES[s.priority]}`}>
                {s.priority}
              </span>
            </div>
            <p className="text-slate-300 text-xs leading-relaxed mt-3">
              <span className="text-emerald-400 font-semibold">Why it may matter:</span> {s.why}
            </p>
            <p className="text-slate-300 text-xs leading-relaxed mt-2">
              <span className="text-gold font-semibold">Recommended next action:</span> {s.action}
            </p>
            <p className="text-slate-400 text-xs leading-relaxed mt-2 flex gap-2">
              <Flag className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" />
              <span><span className="text-slate-300 font-semibold">Limitation:</span> {s.limitation}</span>
            </p>
          </div>
        ))}
      </div>

      <p className="text-slate-500 text-xs leading-relaxed">
        Your actual assessment draws on the business details you provide in the intake form, and the
        full assessment is reviewed by a human before it reaches your portal.
      </p>
    </DialogContent>
  </Dialog>
);

export const SamplePreviewButton = ({ label = "Preview a Sample Assessment", testid = "sample-preview", className }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testid} onClick={() => setOpen(true)} className={className}>
        {label}
      </button>
      <SamplePreviewModal open={open} onOpenChange={setOpen} testid={testid} />
    </>
  );
};

export default SamplePreviewButton;
